import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { PackageCard } from '../components/PackageCard';
import { FiltersPanel } from '../components/FiltersPanel';
import { getPackages } from '../lib/api';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryTitles = {
  'Circuite Externe': 'Circuite Externe',
  'Circuite Romania': 'Circuite România',
  'City Break': 'City Break-uri',
  'Concerte': 'Concerte & Evenimente'
};

const specialTitles = {
  'last-minute': { title: 'Oferte Last Minute', description: 'Reduceri de ultimă oră pentru plecări imediate' },
  'early-booking': { title: 'Early Booking', description: 'Rezervă devreme și economisești' },
  'populare': { title: 'Cele mai populare', description: 'Ofertele preferate de vizitatorii noștri' },
  'ultimele-rezervari': { title: 'Ultimele rezervări', description: 'Ce au rezervat alții recent' }
};

export default function CategoryPage({ category, special, eventType }) {
  const [packages, setPackages] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: null, max: null });
  const [selectedTransport, setSelectedTransport] = useState([]);
  const [sortOrder, setSortOrder] = useState('none');
  const [loading, setLoading] = useState(true);
  // Concert-specific filters
  const [concertFilters, setConcertFilters] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedArtist, setSelectedArtist] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const isConcerts = category === 'Concerte';
  const location = useLocation();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        let params = {};
        
        if (category) {
          params.category = category;
          if (eventType) {
            params.event_type = eventType;
          }
        } else if (special === 'last-minute') {
          params.is_last_minute = true;
        } else if (special === 'early-booking') {
          params.is_early_booking = true;
        }
        
        const pkgs = await getPackages(params);
        
        // For "populare" and "ultimele-rezervari", get all and sort by created_at
        let finalPackages;
        if (special === 'populare' || special === 'ultimele-rezervari') {
          const allPkgs = await getPackages({});
          finalPackages = allPkgs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        } else {
          finalPackages = pkgs;
        }
        
        setPackages(finalPackages);
        
        // Calculate operators count based on filtered packages for this category
        const operatorCounts = {};
        finalPackages.forEach(pkg => {
          if (pkg.operator) {
            operatorCounts[pkg.operator] = (operatorCounts[pkg.operator] || 0) + 1;
          }
        });
        
        // Convert to array format expected by FiltersPanel
        const categoryOperators = Object.entries(operatorCounts).map(([name, count]) => ({
          name,
          count
        }));
        
        setOperators(categoryOperators);
        
        // Load concert filters if on Concerte page
        if (category === 'Concerte') {
          try {
            const filtersRes = await fetch(`${API_URL}/api/concerts/filters`);
            if (filtersRes.ok) setConcertFilters(await filtersRes.json());
          } catch (e) { console.error('Concert filters error:', e); }
        }
        
        // Set initial price range - parsePrice handles string prices
        if (finalPackages.length > 0) {
          const prices = finalPackages.map(p => {
            if (typeof p.price === 'number') return p.price;
            if (typeof p.price === 'string') {
              const match = p.price.replace(/\./g, '').match(/[\d]+/);
              return match ? parseInt(match[0]) : 0;
            }
            return 0;
          }).filter(p => p > 0);
          
          if (prices.length > 0) {
            setPriceRange({
              min: Math.floor(Math.min(...prices)),
              max: Math.ceil(Math.max(...prices))
            });
          }
        }
      } catch (error) {
        console.error('Error loading packages:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // Reset filters when category changes
    setSelectedOperators([]);
    setSelectedTransport([]);
    setSortOrder('none');
    setSelectedGenre('');
    setSelectedArtist('');
    setSelectedCity('');
    setPriceRange({ min: null, max: null });
    window.scrollTo(0, 0);
  }, [category, special, location.pathname]);

  // Helper function to parse price from string or number
  const parsePrice = (price) => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      const match = price.replace(/\./g, '').match(/[\d]+/);
      return match ? parseInt(match[0]) : 0;
    }
    return 0;
  };

  // Filter and sort packages
  const filteredPackages = useMemo(() => {
    let result = [...packages];
    
    // Filter by operator
    if (selectedOperators.length > 0) {
      result = result.filter(pkg => selectedOperators.includes(pkg.operator));
    }
    
    // Filter by price
    if (priceRange.min !== null && priceRange.max !== null) {
      result = result.filter(pkg => {
        const price = parsePrice(pkg.price);
        return price >= priceRange.min && price <= priceRange.max;
      });
    }
    
    // Filter by transport
    if (selectedTransport.length > 0) {
      result = result.filter(pkg => {
        const transport = pkg.transport_type || 'avion';
        return selectedTransport.includes(transport);
      });
    }
    
    // Concert filters
    if (selectedGenre) {
      result = result.filter(pkg => pkg.music_genre === selectedGenre);
    }
    if (selectedArtist) {
      result = result.filter(pkg => pkg.artist_name === selectedArtist);
    }
    if (selectedCity) {
      result = result.filter(pkg => pkg.city === selectedCity);
    }
    
    // Sort by price
    if (sortOrder === 'asc') {
      result.sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
    } else if (sortOrder === 'desc') {
      result.sort((a, b) => parsePrice(b.price) - parsePrice(a.price));
    }
    
    return result;
  }, [packages, selectedOperators, priceRange, selectedTransport, sortOrder, selectedGenre, selectedArtist, selectedCity]);

  const handleResetFilters = () => {
    setSelectedOperators([]);
    setSelectedTransport([]);
    setSortOrder('none');
    setSelectedGenre('');
    setSelectedArtist('');
    setSelectedCity('');
    if (packages.length > 0) {
      const prices = packages.map(p => parsePrice(p.price)).filter(p => p > 0);
      if (prices.length > 0) {
        setPriceRange({
          min: Math.floor(Math.min(...prices)),
          max: Math.ceil(Math.max(...prices))
        });
      }
    }
  };

  const pageTitle = category 
    ? (eventType === 'concert' ? 'Concerte' : (categoryTitles[category] || category))
    : specialTitles[special]?.title || 'Pachete turistice';
  
  const pageDescription = category
    ? (eventType === 'concert' ? 'Concerte muzicale live cu artiști internaționali și români' : `Descoperă cele mai bune oferte de ${category}`)
    : specialTitles[special]?.description || '';

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="category-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white mb-2" data-testid="page-title">
            {pageTitle}
          </h1>
          <p className="text-white/60">{pageDescription}</p>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar - Filters */}
          <aside className="md:w-72 flex-shrink-0">
            <div className="sticky top-24">
              <FiltersPanel
                packages={packages}
                operators={operators}
                selectedOperators={selectedOperators}
                onOperatorChange={setSelectedOperators}
                priceRange={priceRange}
                onPriceChange={setPriceRange}
                selectedTransport={selectedTransport}
                onTransportChange={setSelectedTransport}
                sortOrder={sortOrder}
                onSortChange={setSortOrder}
                onReset={handleResetFilters}
                isConcerts={isConcerts}
                concertFilters={concertFilters}
                selectedGenre={selectedGenre}
                onGenreChange={setSelectedGenre}
                selectedArtist={selectedArtist}
                onArtistChange={setSelectedArtist}
                selectedCity={selectedCity}
                onCityChange={setSelectedCity}
              />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-gold animate-spin" />
              </div>
            ) : filteredPackages.length === 0 ? (
              <div className="text-center py-20 glass rounded-2xl">
                <p className="text-white/60 text-lg mb-2">Nu am găsit pachete</p>
                <p className="text-white/40 text-sm">
                  Încearcă să schimbi filtrele sau să explorezi alte categorii
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4 text-white/60 text-sm">
                  {filteredPackages.length} {filteredPackages.length === 1 ? 'pachet găsit' : 'pachete găsite'}
                  {sortOrder !== 'none' && (
                    <span className="text-gold ml-2">
                      • Sortat după preț {sortOrder === 'asc' ? 'crescător' : 'descrescător'}
                    </span>
                  )}
                </div>
                <div 
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                  data-testid="packages-grid"
                >
                  {filteredPackages.map((pkg, index) => (
                    <div 
                      key={pkg.id} 
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <PackageCard package={pkg} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
